import React, { useEffect, useRef, useState } from "react";
import Header from "../component/Header";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDownloaedProjectById } from "../store/actions/projectAction";
import SizeBox from "../component/Box/SizeBox";
import Button from "../component/Partial/Button";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import Loader from "../component/Partial/Loader";
import JsBarcode from "jsbarcode";
import { getBase64FromUrl } from "../utils/global";
import { prices, codes } from "../utils/mapping";

const MODE = "production";

const modules = {
  1: "2 M",
  2: "4 M",
  3: "6 M",
  4: "8 M",
  6: "12 M",
  9: "18 M",
};

const frameMapping = {
  glass: "Glass",
  acrylic: "Acrylic",
  module_touch_switch: "Modular Touch Switch",
  touch_panel_pro: "Touch panel pro",
  laminate_varient: "Laminate",
};

function DownloadPdf() {
  const params = useParams();
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const introBoxRef = useRef();
  const barcodeRef = useRef(null);
  const user = useSelector((state) => state.project?.user);

  const [downloadAs, setDownloadas] = useState("pdf");
  // Add new state for tracking progress
  const [pdfProgress, setPdfProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  const initDownloadProject = async (id) => {
    try {
      const project = await dispatch(fetchDownloaedProjectById(id)).unwrap();

      // Use Promise.all to wait for all async operations in the map
      let formattedTouches = await Promise.all(
        project?.touches?.map(async (item) => {
          if (!item?.content) {
            return;
          }

          let parseContent = item?.content ? JSON.parse(item?.content) : null;
          const { design, content, selectedIcons } = parseContent;

          const formattedLogo = {
            image: item?.image ? await getBase64FromUrl(item?.image) : null,
            image2: item?.image2 ? await getBase64FromUrl(item?.image2) : null,
            image3: item?.image3 ? await getBase64FromUrl(item?.image3) : null,
          };

          return {
            ...item,
            design: design,
            content: content,
            selectedIcons: selectedIcons,
            logo: formattedLogo,
          };
        })
      );

      setData({
        project: project.project,
        touches: formattedTouches,
      });
    } catch (error) {
      console.log(error);
    }
  };

  // Function to count module usage across all touch panels
  const generateModuleUsageReport = () => {
    if (!data || !data.touches) return {};
    
    const moduleUsage = {};
    const plateUsage = {};
    const topUsage = {};
    
    // Iterate through all touch panels
    data.touches.forEach(touch => {
      if (!touch?.content) return;
      
      // Get design properties for plates and tops
      const design = touch.design;
      if (design) {
        const size = design.size;
        const color = design.color === "#171411" ? "black" : "white";
        const frame = design.frame;
        
        // Add plate based on size and color
        const plateKey = `plate_${size}_${color}`;
        const plateCode = codes.plate[size]?.[color];
        if (plateCode) {
          if (!plateUsage[plateKey]) {
            plateUsage[plateKey] = {
              count: 1,
              code: plateCode,
              size,
              color
            };
          } else {
            plateUsage[plateKey].count = Number(plateUsage[plateKey].count) + 1;
          }
        }
        
        // Add top (glass/acrylic) based on size, color and frame type
        if (frame === 'glass' || frame === 'acrylic') {
          const topKey = `${frame}_${size}_${color}`;
          const topCode = codes.plates_top[frame]?.[color]?.[size];
          if (topCode) {
            if (!topUsage[topKey]) {
              topUsage[topKey] = {
                count: 1,
                code: topCode,
                type: frame,
                size,
                color
              };
            } else {
              topUsage[topKey].count = Number(topUsage[topKey].count) + 1;
            }
          }
        }
      }
      
      // Iterate through each content section in the touch panel
      touch.content.forEach(contentSection => {
        if (!contentSection?.data) return;
        
        // Iterate through each data item (module) in the content section
        contentSection.data.forEach(module => {
          const moduleType = module.value ? module.value : module.type;
          
          if (moduleType) {
            // Get the code based on module type
            let code = "";
            let category = "";
            
            if (codes.module[moduleType]) {
              code = codes.module[moduleType];
              category = "module";
            } else if (codes.accessory[moduleType]) {
              code = codes.accessory[moduleType];
              category = "accessory";
            } else if (moduleType.startsWith("module_touch_switch_")) {
              const normalizedType = moduleType.replace('module_touch_switch_', '');
              code = codes.modularSwitch[normalizedType] || "";
              category = "modularSwitch";
            }
            
            // Increment the count for this module type
            if (!moduleUsage[moduleType]) {
              moduleUsage[moduleType] = {
                count: 1,
                code,
                category
              };
            } else if (typeof moduleUsage[moduleType] === 'object') {
              moduleUsage[moduleType].count = Number(moduleUsage[moduleType].count || 0) + 1;
            } else {
              // Handle the case where moduleUsage[moduleType] is a number (backwards compatibility)
              moduleUsage[moduleType] = {
                count: Number(moduleUsage[moduleType]) + 1,
                code,
                category
              };
            }
          }
        });
      });
    });
    
    return { moduleUsage, plateUsage, topUsage };
  };
  
  // Generate and download CSV with module usage data
  const generateCSV = () => {
    const { moduleUsage, plateUsage, topUsage } = generateModuleUsageReport();
    
    // Create CSV content
    let csvContent = "Type,Item,Count,Code\n";
    
    // Add modules and accessories
    Object.entries(moduleUsage).forEach(([moduleType, data]) => {
      if (typeof data === "number") {
        // Handle the old format for backward compatibility
        csvContent += `Module,${moduleType},${data},""\n`;
      } else {
        // New format with code
        const count = data.count || data;
        const code = data.code || "";
        const category = data.category || "Module";
        csvContent += `${category},${moduleType},${count},"${code}"\n`;
      }
    });
    
    // Add plates
    Object.entries(plateUsage).forEach(([plateKey, data]) => {
      csvContent += `Plate,Size ${modules[data.size]} ${data.color},${data.count},"${data.code}"\n`;
    });
    
    // Add tops (glass/acrylic)
    Object.entries(topUsage).forEach(([topKey, data]) => {
      const typeLabel = data.type.charAt(0).toUpperCase() + data.type.slice(1); // Capitalize first letter
      csvContent += `${typeLabel},Size ${modules[data.size]} ${data.color},${data.count},"${data.code}"\n`;
    });
    
    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${params?.id}_module_usage_report.csv`);
    document.body.appendChild(link);
    
    // Trigger download and clean up
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Function to calculate pricing for a single touch panel
  const calculateTouchPanelPrice = (touchPanel) => {
    if (!touchPanel || !touchPanel.design || !touchPanel.content) return 0;
    
    const design = touchPanel.design;
    let totalPrice = 0;
    
    // Calculate plate price based on size and material
    if (design.frame === 'glass' || design.frame === 'acrylic') {
      // Add plate price
      if (prices.plate[design.size] && prices.plate[design.size][design.frame]) {
        console.log("Frame Price", prices.plate[design.size][design.frame]);
        totalPrice += prices.plate[design.size][design.frame];
      }
      
      // Add module prices
      touchPanel.content.forEach(contentSection => {
        if (!contentSection.data) return;
        
        contentSection.data.forEach(module => {
          const moduleType = module.value || module.type;
          console.log(moduleType);
          // Check if it's a module
          if (prices.module[moduleType]) {
            console.log("Module Price", prices.module[moduleType]);
            totalPrice += prices.module[moduleType];
          }
          // Check if it's an accessory
          else if (prices.accessory[moduleType]) {
            console.log("Accessory Price", prices.accessory[moduleType]);
            totalPrice += prices.accessory[moduleType];
          }
        });
      });
    } 
    // Handle modular touch switches
    else if (design.frame === 'module_touch_switch') {
      touchPanel.content.forEach(contentSection => {
        if (!contentSection.data) return;
        
        contentSection.data.forEach(module => {
          const moduleType = module.value || module.type;
          // Strip the prefix if present
          const normalizedModuleType = moduleType.replace('module_touch_switch_', '');
          
          if (prices.modularSwitch[normalizedModuleType]) {
            totalPrice += prices.modularSwitch[normalizedModuleType];
          }
        });
      });
    }
    
    return totalPrice;
  };

  // Function to generate quotation data
  const generateQuotationData = () => {
    if (!data || !data.touches) return null;
    
    // Calculate project total
    let projectTotal = 0;
    
    // Map to store section costs
    const sectionCosts = new Map();
    
    // Map to store room costs
    const roomCosts = new Map();
    
    // Array to store panel details
    const panelDetails = [];
    
    // Process each touch panel
    data.touches.forEach(touchPanel => {
      if (!touchPanel?.content || !touchPanel?.design) return;
      
      const panelPrice = calculateTouchPanelPrice(touchPanel);
      projectTotal += panelPrice;
      
      // Update section costs
      if (touchPanel.section && touchPanel.section.name) {
        const sectionName = touchPanel.section.name;
        const currentSectionCost = sectionCosts.get(sectionName) || 0;
        sectionCosts.set(sectionName, currentSectionCost + panelPrice);
      }
      
      // Update room costs
      if (touchPanel.room && touchPanel.room.name) {
        const roomName = touchPanel.room.name;
        const sectionName = touchPanel.section?.name || 'Unknown Section';
        const roomKey = `${sectionName} - ${roomName}`;
        const currentRoomCost = roomCosts.get(roomKey) || 0;
        roomCosts.set(roomKey, currentRoomCost + panelPrice);
      }
      
      // Add panel details
      panelDetails.push({
        panelName: touchPanel.name || 'Unnamed Panel',
        sectionName: touchPanel.section?.name || 'Unknown Section',
        roomName: touchPanel.room?.name || 'Unknown Room',
        frameType: frameMapping[touchPanel.design.frame] || touchPanel.design.frame,
        moduleSize: modules[touchPanel.design.size] || touchPanel.design.size,
        color: touchPanel.design.color === "#171411" ? "Black" : "White",
        price: panelPrice
      });
    });
    
    return {
      projectName: data.project.name,
      projectTotal,
      sectionCosts: Array.from(sectionCosts.entries()).map(([name, cost]) => ({ name, cost })),
      roomCosts: Array.from(roomCosts.entries()).map(([key, cost]) => {
        const [sectionName, roomName] = key.split(' - ');
        return { sectionName, roomName, cost };
      }),
      panelDetails
    };
  };
  
  // Generate and download Quotation CSV
  const generateQuotationCSV = () => {
    const quotationData = generateQuotationData();
    if (!quotationData) return;
    
    // Create CSV content
    let csvContent = "QUOTATION FOR PROJECT: " + quotationData.projectName + "\n\n";
    
    // Project total
    csvContent += "PROJECT TOTAL,,,,,," + quotationData.projectTotal + "\n\n";
    
    // Section costs
    csvContent += "SECTION COSTS:\n";
    csvContent += "Section Name,,,,,,Cost\n";
    quotationData.sectionCosts.forEach(section => {
      csvContent += `${section.name},,,,,,${section.cost}\n`;
    });
    csvContent += "\n";
    
    // Room costs
    csvContent += "ROOM COSTS:\n";
    csvContent += "Section Name,Room Name,,,,,Cost\n";
    quotationData.roomCosts.forEach(room => {
      csvContent += `${room.sectionName},${room.roomName},,,,,${room.cost}\n`;
    });
    csvContent += "\n";
    
    // Panel details
    csvContent += "TOUCH PANEL DETAILS:\n";
    csvContent += "Panel Name,Section,Room,Frame Type,Module Size,Color,Price\n";
    quotationData.panelDetails.forEach(panel => {
      csvContent += `${panel.panelName},${panel.sectionName},${panel.roomName},${panel.frameType},${panel.moduleSize},${panel.color},${panel.price}\n`;
    });
    
    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${params?.id}_quotation.csv`);
    document.body.appendChild(link);
    
    // Trigger download and clean up
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generatePDF = async (touches, projectDetails) => {
    setIsGenerating(true);
    setPdfProgress(0);

    // Create barcode canvas
    const barCodeCanvas = window.document.createElement("canvas");
    JsBarcode(barCodeCanvas, projectDetails?._id, {
      format: "CODE128",
      lineColor: "black",
      width: 1,
      height: 30,
      displayValue: true,
    });

    const pdf = new jsPDF({
      unit: "mm",
      orientation: "landscape",
      format: [1000, 550], // Custom width and height
      putOnlyUsedFonts: true,
      floatPrecision: 16,
    });

    // Set background color
    pdf.setFillColor(23, 20, 17); // RGB for #171411
    pdf.rect(
      0,
      0,
      pdf.internal.pageSize.width,
      pdf.internal.pageSize.height,
      "F"
    );

    // Add project title to first page
    pdf.setTextColor(255, 255, 255); // white color
    pdf.setFontSize(100);
    
    pdf.setFontSize(60);
    pdf.text("Project Name", pdf.internal.pageSize.width / 2, 120, {
      align: "center",
    });

    pdf.setFontSize(80);
    pdf.text(
      data?.project?.name || "No Project Name",
      pdf.internal.pageSize.width / 2,
      190,
      { align: "center" }
    );

    // Calculate total items to process for progress tracking
    const validTouches = touches.filter(item => item?.content);
    const totalPagesToProcess = validTouches.length + 1; // +1 for barcode page
    let pagesProcessed = 1; // Start with 1 for cover page
    setPdfProgress(Math.round((pagesProcessed / totalPagesToProcess) * 100));

    // Define the batch size - how many panels to process at once
    const BATCH_SIZE = 10;
    
    // Process panels in batches to prevent browser from becoming unresponsive
    for (let i = 0; i < validTouches.length; i += BATCH_SIZE) {
      const batch = validTouches.slice(i, i + BATCH_SIZE);
      
      // Process each batch
      for (const item of batch) {
        let element = document.getElementById(`main-board-${item?._id}`);

        if (!element) {
          continue;
        }

        let { design, content, _id, section, room } = item;

        // If multi-panel layout is being used (2 panels per page)
        const useMultiPanelLayout = content?.length <= 2 && i + 1 < validTouches.length;
        
        // Different dimensions based on content length or layout strategy
        const PAGE_DIMANSION = {
          0: {
            width: 800,
            height: 1000,
            yPosition: 600,
          },
          1: {
            width: 400,
            height: 1000,
            yPosition: 250,
          },
          2: {
            width: 550,
            height: 1000,
            yPosition: 400,
          },
          3: {
            width: 700,
            height: 1000,
            yPosition: 550,
          },
        };

        const dimansion = PAGE_DIMANSION[content?.length];
        
        // Add a new page for each panel (or pair of panels in multi-layout)
        if (!useMultiPanelLayout || (i % 2 === 0)) {
          pdf.addPage([dimansion?.width, dimansion?.height]);
        }

        // Render the panel to canvas
        const canvas = await html2canvas(element, {
          useCORS: true,
          scale: 3, // Reduced scale for better performance while maintaining quality
          logging: false, // Disable logging for performance
          backgroundColor: null,
        });

        // Compress the image data
        const imgData = canvas.toDataURL("image/jpeg", 0.85); // Use JPEG with 85% quality for smaller size
        
        // Calculate position for single or multi-panel layout
        let xOffset = 0;
        let yOffset = 0;
        
        if (useMultiPanelLayout && (i % 2 === 1)) {
          // For second panel in multi-panel layout
          xOffset = dimansion?.width / 2;
        }
        
        const x = (pdf.internal.pageSize.width - canvas.width / 4) / 2;
        const y = 10; // You can adjust this if needed

        // Add panel image with reduced size
        pdf.addImage(imgData, "JPEG", x, y, canvas.width / 5, canvas.height / 5);
        
        // Calculate text positioning
        let yPosition = dimansion?.yPosition / 1.2; // Adjust for multi-panel layout
        if (useMultiPanelLayout && (i % 2 === 1)) {
          yPosition = dimansion?.yPosition / 1.2;
        }
        
        // Add panel details with smaller font size
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(30); // Reduced font size
        
        const textX = xOffset + 20;
        
        pdf.text(`Project: ${projectDetails?.name}`, textX, yPosition);

        yPosition += 20; // Reduced spacing
        pdf.text(`Section: ${section?.name}`, textX, yPosition);

        yPosition += 20;
        pdf.text(`Room: ${room?.name}`, textX, yPosition);

        yPosition += 20;
        pdf.setFont(undefined, 'bold');
        pdf.text(`Panel: ${item?.name}`, textX, yPosition);
        pdf.setFont(undefined, 'normal');

        yPosition += 20;
        pdf.text(
          `Type: ${frameMapping[design?.frame]}`,
          textX,
          yPosition
        );
        
        const moduleLabel = modules[design?.size] ?? "0";
        yPosition += 20;
        pdf.text(`Size: ${moduleLabel}`, textX, yPosition);

        yPosition += 20;
        pdf.text(
          `Color: ${design?.color == "#171411" ? "Black" : "White"}`,
          textX,
          yPosition
        );
        
        // Update progress after each panel is processed
        pagesProcessed++;
        setPdfProgress(Math.round((pagesProcessed / totalPagesToProcess) * 100));
        
        // Add a small delay to prevent UI freezing and allow progress updates to render
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    // Add barcode page at the end
    pdf.addPage([200, 1000]);
    const barcodeImage = barCodeCanvas.toDataURL("image/png");
    const barcodeWidth = 200; // Reduced from 200
    const barcodeHeight = 100; // Reduced from 100
    const xPosition = (pdf.internal.pageSize.width - barcodeWidth) / 2;
    const yPosition = (pdf.internal.pageSize.height - barcodeHeight) / 2;
    pdf.addImage(
      barcodeImage,
      "PNG",
      xPosition,
      yPosition,
      barcodeWidth,
      barcodeHeight
    );

    // Final progress update
    setPdfProgress(100);
    
    // Get the PDF as a blob and create a download link
    const pdfBlob = pdf.output("blob");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(pdfBlob);
    link.download = `${projectDetails?.name}.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
    
    // Small delay before setting isGenerating to false
    setTimeout(() => {
      setIsGenerating(false);
    }, 500);
  };

  // const generatePng = async (touches, projectDetails) => {
  //   for (const item of touches) {
  //     let element = document.getElementById(`main-board-${item?._id}`);

  //     if (element) {
  //       // Create a wrapper div with padding
  //       const wrapper = document.createElement("div");
  //       wrapper.style.padding = "20px";
  //       wrapper.style.backgroundColor = "white";
  //       wrapper.style.display = "inline-block";

  //       const clone = element.cloneNode(true);
  //       wrapper.appendChild(clone);

  //       document.body.appendChild(wrapper);

  //       try {
  //         // Capture the wrapper
  //         const canvas = await html2canvas(wrapper, {
  //           useCORS: true,
  //         });
  //         const imgData = canvas.toDataURL("image/png");

  //         if (MODE == "dev") {
  //           const newTab = window.open();
  //           newTab.document.write(<img src="${imgData}" alt="Canvas Image" />);
  //         } else {
  //           const link = document.createElement("a");
  //           link.href = imgData;
  //           link.download = `${item?.name}.png`;
  //           link.click();
  //           URL.revokeObjectURL(link.href);
  //         }
  //       } finally {
  //         document.body.removeChild(wrapper);
  //       }
  //     }
  //   }
  // };

  const generatePng = async (touches, projectDetails) => {
    for (const item of touches) {
      let element = document.getElementById(`main-board-${item?._id}`);
      // let { design } = item;

      if (element) {
        // Create a wrapper div with padding, but no background color
        const wrapper = document.createElement("div");
        // if (design?.frame != "module_touch_switch") {
        //   wrapper.style.padding = "20px";
        // }
        wrapper.style.display = "inline-block";

        const clone = element.cloneNode(true);

        // Remove background from the clone and its children
        const removeBackground = (el) => {
          if (!el.classList.contains("bg-remove-expaction")) {
            el.style.backgroundColor = "transparent";
          }
          Array.from(el.children).forEach((child) => removeBackground(child));
        };
        removeBackground(clone);

        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);

        try {
          // Capture the wrapper with transparency enabled
          const canvas = await html2canvas(wrapper, {
            backgroundColor: null,
            removeContainer: true,
            useCORS: true,
            allowTaint: true,
          });
          const imgData = canvas.toDataURL("image/png");

          if (MODE == "dev") {
            const newTab = window.open();
            newTab.document.write(<img src="${imgData}" alt="Canvas Image" />);
          } else {
            const link = document.createElement("a");
            link.href = imgData;
            link.download = `${projectDetails?._id}__${item?.name}.png`;
            link.click();
            URL.revokeObjectURL(link.href);
          }
        } finally {
          document.body.removeChild(wrapper);
        }
      }
    }
  };
  useEffect(() => {
    if (params?.id) {
      initDownloadProject(params?.id);
    }

    // document.body.style.overflow = "hidden";
  }, []);

  if (!data) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col gap-5">
      <Header />

      <div className="flex flex-col gap-5 mt-28 ">
        <div className="w-full flex justify-end gap-3">
          {user?.role == "admin" && (
            <Button
              text="Download icon  png"
              className="px-3"
              onClick={() => {
                setDownloadas("icon-png");
                setTimeout(() => {
                  generatePng(data?.touches, data?.project);
                }, 1000);
              }}
            />
          )}
          {user?.role == "admin" && (
            <Button
              text="Download cutout png"
              className="px-3"
              onClick={() => {
                setDownloadas("cutout-png");
                setTimeout(() => {
                  generatePng(data?.touches, data?.project);
                }, 1000);
              }}
            />
          )}
          {user?.role == "admin" && (
            <Button
              text="Download CSV"
              className="px-3"
              onClick={() => {
                generateCSV();
              }}
            />
          )}
          {user?.role == "admin" && (
            <Button
              text="Download Quotation"
              className="px-3"
              onClick={() => {
                generateQuotationCSV();
              }}
            />
          )}

          <Button
            text={isGenerating ? "Generating..." : "Download pdf"}
            className="px-3 mr-5"
            disabled={isGenerating}
            onClick={() => {
              setDownloadas("pdf");
              setTimeout(() => {
                generatePDF(data?.touches, data?.project);
              }, 1000);
            }}
          />
        </div>
        
        {/* Progress bar for PDF generation */}
        {isGenerating && (
          <div className="w-full px-5">
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div 
                className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out" 
                style={{width: `${pdfProgress}%`}}
              ></div>
            </div>
            <p className="text-center text-sm">{pdfProgress}% - Generating PDF...</p>
          </div>
        )}

        <div
          className="w-full h-screen"
          ref={introBoxRef}
          style={{
            backgroundColor: "#171411",
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <h3 style={{ color: "white", fontSize: "1.5rem" }}>Project Name</h3>
            <h4 style={{ color: "white", fontSize: "2rem" }}>
              {data?.project?.name}
            </h4>
          </div>
        </div>
        <div className="flex flex-col gap-5">
          {data &&
            data?.touches?.map((item) => {
              if (!item?.content) {
                return null;
              }

              let { design, content, _id, section, room, selectedIcons, logo } =
                item;

              if (!design || !content) {
                return null;
              }

              const moduleLabel = modules[design?.size] ?? "0";

              return (
                <div className="flex flex-col gap-5" key={_id}>
                  <SizeBox
                    {...design}
                    modifiedHandleDrop={() => {}}
                    handleDragOver={() => {}}
                    handleDeleteElement={() => {}}
                    content={content}
                    selectedIcons={selectedIcons}
                    isDownloadMode={downloadAs === "pdf" ? false : true}
                    id={_id}
                    downloadAs={downloadAs}
                    logo={logo}
                  />

                  <div className="w-full p-5">
                    <h3 className="text-2xl">Touch Panel Details</h3>
                    <div className="w-3/4 flex flex-wrap gap-2">
                      <p>
                        <span className="font-bold text-lg">Project Name:</span>
                        {data?.project?.name}
                      </p>
                      <p>
                        <span className="font-bold text-lg">Section Name:</span>
                        {section?.name}
                      </p>
                      <p>
                        <span className="font-bold text-lg">Room Name:</span>
                        {room?.name}
                      </p>
                      <p>
                        <span className="font-bold text-lg">Panel Name:</span>
                        {item?.name}
                      </p>
                      <p>
                        <span className="font-bold text-lg">
                          Touch Panel Type:
                        </span>
                        {frameMapping[design?.frame]}
                      </p>
                      <p>
                        <span className="font-bold text-lg">Module Size:</span>
                        {moduleLabel}
                      </p>
                      <p>
                        <span className="font-bold text-lg">Colour:</span>
                        {design?.color == "#171411" ? " Black" : " White"}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default DownloadPdf;
